import { type InviteAcceptance, inviteAcceptanceSchema } from "@crux/contracts";
import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Who is in a workspace, and the owner's calls to change that.
 *
 * A 409 from a membership change is the server refusing to leave a workspace
 * without an owner; its message says so in words a person can act on, so it
 * is passed through rather than replaced.
 */

export type MembersList =
  paths["/api/workspaces/{workspaceId}/members"]["get"]["responses"][200]["content"]["application/json"];
export type Member = MembersList["members"][number];
export type PendingInvite = MembersList["invites"][number];
export type CreatedInvite =
  paths["/api/workspaces/{workspaceId}/invites"]["post"]["responses"][201]["content"]["application/json"];
export type InvitePreview =
  paths["/api/invites/{token}"]["get"]["responses"][200]["content"]["application/json"];
export type GrantableRole = "owner" | "member";

const onWorkspace = (workspaceId: string) => ({ path: { workspaceId } });

export async function fetchMembers(workspaceId: string): Promise<MembersList> {
  const { data, response } = await api.GET("/api/workspaces/{workspaceId}/members", {
    params: onWorkspace(workspaceId),
  });
  if (data === undefined) throw new Error(problem(response.status, "load the members"));
  return data;
}

export async function createInvite(
  workspaceId: string,
  input: { readonly email: string; readonly role: GrantableRole },
): Promise<CreatedInvite> {
  const { data, response } = await api.POST("/api/workspaces/{workspaceId}/invites", {
    params: onWorkspace(workspaceId),
    body: input,
  });
  if (data !== undefined) return data;
  if (response.status === 400)
    throw new Error("That does not look like an email address.");
  if (response.status === 429) {
    throw new Error("Too many invites at once. Wait fifteen minutes and try again.");
  }
  throw new Error(problem(response.status, "create the invite"));
}

export async function revokeInvite(workspaceId: string, inviteId: string): Promise<void> {
  const { response } = await api.DELETE(
    "/api/workspaces/{workspaceId}/invites/{inviteId}",
    { params: { path: { workspaceId, inviteId } } },
  );
  if (!response.ok) throw new Error(problem(response.status, "revoke the invite"));
}

export async function removeMember(workspaceId: string, userId: string): Promise<void> {
  const { error, response } = await api.DELETE(
    "/api/workspaces/{workspaceId}/members/{userId}",
    { params: { path: { workspaceId, userId } } },
  );
  if (response.ok) return;
  if (response.status === 409 && error !== undefined) throw new Error(error.message);
  throw new Error(problem(response.status, "remove them"));
}

/** Null for a link that was never issued; the page says so plainly. */
export async function fetchInvite(token: string): Promise<InvitePreview | null> {
  const { data, response } = await api.GET("/api/invites/{token}", {
    params: { path: { token } },
  });
  if (response.status === 404) return null;
  if (data === undefined) throw new Error(problem(response.status, "open this invite"));
  return data;
}

/**
 * An invite link opened while signed in: the server joins its holder when the
 * invite is for their address. Only the Rust API has this route, so — like
 * `people.ts` — it is a plain request parsed with the shared schema.
 *
 * Safe to send twice: someone the invite already let in is answered `joined`.
 */
export async function acceptInvite(token: string): Promise<InviteAcceptance> {
  const response = await fetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(problem(response.status, "join this workspace"));
  return inviteAcceptanceSchema.parse(await response.json());
}

/**
 * Where "Continue with Google" goes: a real navigation, because the API
 * answers with a redirect to Google.
 */
export function googleSignInHref(
  options: { readonly invite?: string; readonly switchAccount?: boolean } = {},
): string {
  const query = new URLSearchParams();
  if (options.invite !== undefined) query.set("invite", options.invite);
  if (options.switchAccount === true) query.set("switch", "1");
  const suffix = query.toString();
  return `/api/auth/google/start${suffix === "" ? "" : `?${suffix}`}`;
}
